import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Tooltip, IconButton, Grid, Card, CardContent, CardActions } from '@mui/material';
import { ArrowForward as ArrowForwardIcon, Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetProfileQuery } from '../../features/api/rest/apiProfileSlice';
import { useGetOrganizationUserQuery } from '../../features/api/rest/apiOrganizationsSlice';
import AddLotDialog from './AddLotDialog';
import AddCodeDialog from './AddCodeDialog';
import DeleteLotDialog from './DeleteLotDialog';

function RootLotCard({ ean, opk, id, authority, code, from = null, to = null }) {
  const formatDate = (dateToFormat) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateToFormat).toLocaleDateString('it-IT', options);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { data: profile } = useGetProfileQuery();
  const { data: users = [] } = useGetOrganizationUserQuery(opk);

  let SystemAdmin = false;
  let OrgAdmin = false;
  const permission = () => {
    if (profile.is_staff) {
      SystemAdmin = true;
    }
    OrgAdmin = users.some((user) => user.is_admin && profile.id === user.user);
    if (OrgAdmin || SystemAdmin) return true;
    return false;
  };

  const goToLot = () => {
    navigate(`/profile/organizations/${opk}/products/${ean}/lots/${id}/lotTree`);
  };

  return (
    <>
      <Card className="Product_Card">
        <Tooltip title={t('organizations.product.lots.goToLot')} placement="top">
          <IconButton onClick={goToLot} sx={{ position: 'absolute', top: 1, right: 1 }}>
            <ArrowForwardIcon />
          </IconButton>
        </Tooltip>
        <CardContent sx={{ p: 1 }} onClick={goToLot}>
          <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              variant="h7"
              sx={{
                paddingTop: '1em',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {authority} - {code}
            </Typography>
            <Grid
              container
              sx={{
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                textAlign: 'initial',
                overflow: 'hidden',
                wordBreak: 'break-word',
                minHeight: '3em',
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: 13 }}>
                {t('organizations.product.lots.validity')}: {from && formatDate(from)} - {to && formatDate(to)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        {permission() && (
          <CardActions sx={{ m: 0, p: 0 }}>
            <Grid container alignItems="center" sx={{ gap: 2, display: 'flex', justifyContent: 'space-evenly' }}>
              <Grid item>
                <Tooltip title={t('common.actions.edit')} placement="top">
                  <IconButton onClick={() => setIsEditing(true)}>
                    <EditIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title={t('organizations.product.lots.addCodes.dialog.title')} placement="top">
                  <IconButton onClick={() => setIsAdding(true)}>
                    <AddIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title={t('common.actions.delete')} placement="top">
                  <IconButton onClick={() => setIsDeleting(true)}>
                    <DeleteIcon sx={{ fontSize: 25 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardActions>
        )}
      </Card>
      <AddCodeDialog ean={ean} opk={opk} lpk={id} isOpen={isAdding} setIsOpen={setIsAdding} />
      <AddLotDialog action="edit" ean={ean} opk={opk} id={id} isOpen={isEditing} setIsOpen={setIsEditing} authority={authority} code={code} from={from} to={to} />
      <DeleteLotDialog isOpen={isDeleting} setIsOpen={setIsDeleting} ean={ean} opk={opk} id={id} />
    </>
  );
}

export default RootLotCard;

RootLotCard.propTypes = {
  ean: PropTypes.string.isRequired,
  opk: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  authority: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  from: PropTypes.string,
  to: PropTypes.string,
};
